import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';

import { ScreenShare } from '@mui/icons-material';
import MicOffIcon from '@mui/icons-material/MicOff';
import MicIcon from '@mui/icons-material/Mic';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';

import HomeBarButton from '../../HomeBarButton';
import WarningButtonBadge from '../../WarningButtonBadge';
import WebcamSharingButton from './WebcamSharing';
import SettingsButton from '../SettingsButton';

import { useConference } from '../../DashboardContent/Dolby';
import { useMytaverseEvent } from '../../../providers';
import { useIsSpeakerMutedNotificationShown } from '../../DashboardContent/Dolby/hooks';
import { useModal } from '../../../../../hooks/modal';
import { useShowInitialMuteModal } from './hooks';
import { useNotification } from '../../../../../components/Notification';

import {
  sendCustomSentryBreadcrumb,
  setSentryError,
} from '../../../../../helpers/sentry';
import { getCatchErrorMessage } from '../../../../../helpers/error';

import { useStyles } from './styles';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import OfficeHoursButton from '../../../../../ek/modules/dashboard/components/HomeBottomBar/OfficeHoursButton';
import { useZoom } from '../../DashboardContent/Zoom/ZoomProvider';
import { getErrorNotification } from '../../../../../components/Notification/helpers';
import { AnalyticsType } from '../../../../../interfaces/analytics';
import { env } from '../../../../../env';

const HomeBottomButtonOptions = () => {
  const classes = useStyles();

  const {
    isConferenceInitialized,
    isFirstPersonView,
    setIsFirstPersonView,
    disabledPersonView,
    isConferenceListener,
    ueScreenScreenName,
    ueScreenMediaStreams,
  } = useConference();
  const {
    muted,
    setMuted,
    currentEvent,
    currentParticipant,
    canSpeak,
    canShowCamera,
    canShareCamera,
    canShareScreen,
    trackEventAnalytics,
  } = useMytaverseEvent();

  const {
    startScreenSharing,
    stopScreenSharing,
    isSharingScreenToUE,
    isInConference,
    isJoiningConference,
    micList,
    isMicrophonePermissionGranted,
  } = useZoom();

  const { sendMessageToUnreal } = useMytaverse();
  const { speakerMutedNotification, resetSpeakerMutedNotification, showPulse } =
    useIsSpeakerMutedNotificationShown(isConferenceInitialized, muted);
  const { t: translate } = useTranslation('common');
  const { showNotification } = useNotification();
  const isViewToggle = currentEvent?.eventTags?.some(
    (tag) => tag === env.REACT_APP_VIEW_TOGGLE_EVENT_TAG,
  );

  const initialMuteModalData = useModal();

  const currentParticipantId = currentParticipant?.participantId || '';
  const microDeviceId = micList.length ? micList[0].deviceId : '';

  // const showDolbyShareScreen = !!currentParticipant?.isSpeaker;

  const currentScreenSharing = !!ueScreenMediaStreams.find(
    (s) => s.participantId === currentParticipantId,
  );

  useShowInitialMuteModal({
    eventId: currentEvent?.eventId || '',
    initialMuteModalData,
  });

  const handleMicroClick = () => {
    if (!microDeviceId) {
      showNotification(
        getErrorNotification({
          message: translate(
            'notifications.microphone.start.userForbiddenCapture',
          ),
        }),
      );
    } else {
      sendCustomSentryBreadcrumb({
        message: `${!muted ? 'mute' : 'unmute'} micro`,
      });
      setMuted();
      resetSpeakerMutedNotification(speakerMutedNotification);
    }
  };

  const handleShareScreenClick = useCallback(async () => {
    try {
      if (isSharingScreenToUE) {
        sendCustomSentryBreadcrumb({ message: 'Stop zoom share screen' });
        console.log('Stop zoom share screen');
        await stopScreenSharing();
        trackEventAnalytics(AnalyticsType.ScreenShareFinish);
      } else {
        sendCustomSentryBreadcrumb({ message: 'Start zoom share screen' });
        console.log('Start zoom share screen');
        await startScreenSharing();
        trackEventAnalytics(AnalyticsType.ScreenShareStart);
      }
    } catch (error: unknown) {
      console.log(getCatchErrorMessage(error));
      setSentryError(error);
      showNotification(
        getErrorNotification({
          message: translate('notifications.video.sharing.stopped'),
        }),
      );
    }
  }, [
    isSharingScreenToUE,
    trackEventAnalytics,
    startScreenSharing,
    stopScreenSharing,
    sendCustomSentryBreadcrumb,
  ]);

  const handleSetPersonView = useCallback(() => {
    const newStateIsFirstPersonView = !isFirstPersonView.flag;

    sendMessageToUnreal({
      action: 'TOGGLE_VIEW',
      view: newStateIsFirstPersonView ? 'FIRST_PERSON' : 'THIRD_PERSON',
    });

    // setIsFirstPersonView((prevState) => ({
    //   ...prevState,
    //   flag: newStateIsFirstPersonView,
    // }));
  }, [sendMessageToUnreal, setIsFirstPersonView, isFirstPersonView]);

  const translatedMicTooltip =
    microDeviceId || isMicrophonePermissionGranted
      ? 'tooltips.toggleMic'
      : 'tooltips.warningPermissionCam';

  const translatedShareTooltip =
    isSharingScreenToUE && currentScreenSharing
      ? 'tooltips.stopShareScreen'
      : 'tooltips.shareScreen';
  return (
    <>
      {canSpeak ? (
        <>
          {canShowCamera || canShareCamera ? <WebcamSharingButton /> : ''}

          <HomeBarButton
            label={translate(translatedMicTooltip)}
            handleClick={handleMicroClick}
            showPulseAnimation={!!showPulse}
            isRed={muted || !microDeviceId}
            disabled={!isInConference || isConferenceListener}
            isLoading={isJoiningConference}
            icon={
              <>
                {muted || !isMicrophonePermissionGranted ? (
                  <WarningButtonBadge
                    classes={{
                      root: classes.badge(isMicrophonePermissionGranted),
                    }}
                  >
                    <MicOffIcon />
                  </WarningButtonBadge>
                ) : (
                  <MicIcon />
                )}
              </>
            }
          />

          {canShareScreen && !isMobile && ueScreenScreenName && (
            <HomeBarButton
              label={translate(translatedShareTooltip)}
              isRed={!isSharingScreenToUE || !currentScreenSharing}
              icon={<ScreenShare />}
              isLoading={isJoiningConference}
              disabled={
                !isInConference ||
                (!!ueScreenMediaStreams.length && !currentScreenSharing)
              }
              handleClick={handleShareScreenClick}
            />
          )}
        </>
      ) : (
        ''
      )}

      <HomeBarButton
        label="Toggle Person View"
        icon={isFirstPersonView.flag ? <PersonIcon /> : <VisibilityIcon />}
        disabled={disabledPersonView}
        handleClick={handleSetPersonView}
      />
      <SettingsButton />
      <OfficeHoursButton />
    </>
  );
};

export default HomeBottomButtonOptions;
